import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
interface PilotSlide4Props {
  isDark?: boolean;
}
export function PilotSlide4({
  isDark = false
}: PilotSlide4Props) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <motion.div className="h-full w-full overflow-y-auto overflow-x-hidden" variants={containerVariants} initial="hidden" animate="visible" data-id="element-662">
      <div className="min-h-full flex flex-col justify-start md:justify-center max-w-6xl mx-auto px-6 sm:px-12 py-20 sm:py-16" data-id="element-663">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-10 sm:mb-12" data-id="element-664">
          <h1 className={`text-3xl sm:text-5xl font-serif font-medium mb-3 sm:mb-4 tracking-tight ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-665">
            The Pilot Offer
          </h1>
          <p className={`text-sm sm:text-base px-4 ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-666">
            90-day pilot program with full platform access and dedicated support
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" data-id="element-667">
          {/* Left Column - Pricing and Program Details */}
          <motion.div variants={itemVariants} data-id="element-668">
            <div className={`backdrop-blur-md rounded-3xl p-6 sm:p-8 h-full shadow-lg ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-gradient-to-br from-heliona-navy/10 to-heliona-slate/5 border border-heliona-navy/20'}`} data-id="element-669">
              <div className="mb-6 sm:mb-7" data-id="element-670">
                <p className={`text-xs sm:text-sm uppercase tracking-wider mb-3 font-semibold ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-671">
                  PILOT PROGRAM
                </p>
                <div className="flex items-baseline gap-2.5 mb-2" data-id="element-672">
                  <span className={`text-4xl sm:text-5xl font-bold ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-673">
                    $5,000
                  </span>
                  <span className={`text-sm sm:text-base ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-674">
                    per engagement
                  </span>
                </div>
                <p className={`text-sm sm:text-base font-semibold mb-2 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-675">
                  90-day engagement
                </p>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-676">
                  Covers 5 leader seats. Additional leaders available at a
                  discounted fee upon request.
                </p>
              </div>

              <div className="space-y-3 sm:space-y-3.5 mb-6 sm:mb-7" data-id="element-677">
                <div className="flex items-start gap-3 bg-heliona-mint/10 -mx-3 px-3 py-2.5 rounded-xl border border-heliona-mint/30 shadow-sm" data-id="element-678">
                  <Sparkles className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-679" />
                  <span className={`text-xs sm:text-sm leading-relaxed font-semibold ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-680">
                    Premium executive coaches throughout the 90 days (pilot fee
                    covers coach access for your leaders)
                  </span>
                </div>
                {['Full platform access (Helium Score ™, Helium Care ™, Helium Stream ™)', 'White-glove onboarding', 'Dedicated customer success check-ins', 'Executive dashboard and reporting'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-681">
                    <Check className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-682" />
                    <span className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-683">
                      {item}
                    </span>
                  </div>)}
              </div>

              <div className={`pt-5 sm:pt-6 border-t ${isDark ? 'border-white/10' : 'border-heliona-navy/20'}`} data-id="element-684">
                <p className={`text-xs sm:text-sm ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-685">
                  Pilot starts with 5 leaders (additional leaders at discounted
                  rate upon request)
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Column - What's Included & Incentives */}
          <div className="space-y-5 sm:space-y-6" data-id="element-686">
            <motion.div variants={itemVariants} data-id="element-687">
              <div className={`rounded-3xl p-5 sm:p-7 shadow-sm ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-white border border-heliona-beige'}`} data-id="element-688">
                <h2 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-5 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-689">
                  What's Included
                </h2>
                <div className="space-y-3" data-id="element-690">
                  <div className="flex items-start gap-3 bg-heliona-mint/10 -mx-2 px-2 py-2.5 rounded-xl border border-heliona-mint/30" data-id="element-691">
                    <Sparkles className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-692" />
                    <span className={`text-xs sm:text-sm leading-relaxed font-semibold ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-693">
                      Premium executive coaches throughout the 90 days (pilot
                      fee covers coach access for your leaders)
                    </span>
                  </div>
                  {['Full access to Heliona platform (Helium Score ™, Helium Care ™, Helium Stream ™)', 'White-glove onboarding', 'Dedicated customer success check-ins', 'Pilot starts with 5 leaders (additional leaders at discounted rate upon request)'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-694">
                      <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-heliona-mint' : 'text-heliona-mint-dark'}`} data-id="element-695" />
                      <span className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white/80' : 'text-heliona-text-gray'}`} data-id="element-696">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} data-id="element-697">
              <div className={`rounded-3xl p-5 sm:p-7 mb-16 sm:mb-0 shadow-sm ${isDark ? 'bg-heliona-orange/10 border border-heliona-orange/30' : 'bg-gradient-to-br from-heliona-orange/10 to-heliona-orange/5 border border-heliona-orange/30'}`} data-id="element-698">
                <h2 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-5 ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-699">
                  Exclusive Incentives
                </h2>
                <div className="space-y-3" data-id="element-700">
                  {['50% pilot fee credited toward annual plan', 'Early Adopter 10% lifetime discount locked in', '"Skin in the Game" reward challenge (Heliona Recharge Experience for the top-improving team)'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-701">
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-heliona-orange flex-shrink-0 mt-0.5" data-id="element-702" />
                      <span className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-white' : 'text-heliona-charcoal'}`} data-id="element-703">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>;
}